import React from 'react';
import { PropTypes } from 'prop-types';
import { Paper, TableBody, TableRow, Table } from '@mui/material';
import { PortalButton, Text } from '@custom-components';
import {
  MenuWrapper,
  SecondaryActionsWrapper,
  TitleContainer,
  ValueContainer,
  TableWrapper
} from '../MultiSearchSelect.styles';
import MultiSearchSelectMenuItems from './MultiSearchSelectMenuItems';
import TwoLayerMultiSearchSelectMenuItems from './TwoLayerMultiSearchSelectMenuItems';

function MultiSearchSelectMenu(props) {
  const {
    name,
    isOpen,
    filteredOptions,
    filteredTwoLayerOptions,
    menuProps,
    handleChange,
    emptyMessage,
    removeAll,
    inputProps,
    enableActions,
    selectAll,
    value,
    minCharacters,
    clearable,
    allSelectable,
    selectAllGuests,
    showSearchCount,
    singleSelectOnly
  } = props;
  const count = value.length;
  const searchValue = inputProps.value;
  const belowMinimumCharacters = searchValue.length < minCharacters + 1;
  const hasSearchValue = searchValue.length > 0;

  const hasNoValue = !value?.length;
  const hasNoFilteredOptions = !filteredOptions?.length && !Object.keys(filteredTwoLayerOptions).length;
  const nonClearable = !clearable;

  const disabledRemove =
    nonClearable &&
    (hasSearchValue ? belowMinimumCharacters || hasNoValue || hasNoFilteredOptions : hasNoFilteredOptions);
  const disabledAdd = hasSearchValue && (belowMinimumCharacters || hasNoFilteredOptions);

  const actionType = searchValue?.length === 0 ? 'All' : 'Filtered';

  return (
    <div {...menuProps} style={{ position: 'relative' }}>
      {isOpen && (
        <Paper
          elevation={2}
          style={{
            position: 'absolute',
            zIndex: 9999,
            width: '100%',
            top: '100%'
          }}
        >
          <MenuWrapper>
            {filteredOptions?.length ? (
              <MultiSearchSelectMenuItems
                filteredOptions={filteredOptions}
                name={name}
                value={value}
                handleChange={handleChange}
                singleSelectOnly={singleSelectOnly}
              />
            ) : (
              Object.entries(filteredTwoLayerOptions).map(
                ([title, options]) =>
                  options?.length && (
                    <TwoLayerMultiSearchSelectMenuItems
                      options={options}
                      name={name}
                      value={value}
                      optionsTitle={title}
                      handleChange={handleChange}
                    />
                  )
              )
            )}
            {hasNoFilteredOptions && (
              <Text.CopyParagraph noMargins align="center" color="tertiary">
                {emptyMessage}
              </Text.CopyParagraph>
            )}
          </MenuWrapper>
          <SecondaryActionsWrapper>
            {showSearchCount && (
              <TableWrapper>
                <Table padding="none">
                  <TableBody>
                    <TableRow style={{ height: 0 }}>
                      <TitleContainer>Selected</TitleContainer>
                      <ValueContainer>{count}</ValueContainer>
                    </TableRow>
                    <TableRow style={{ height: 0 }}>
                      <TitleContainer>Filtered</TitleContainer>
                      <ValueContainer>
                        {searchValue?.length > minCharacters ? filteredOptions?.length : 0}
                      </ValueContainer>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableWrapper>
            )}

            {allSelectable && (
              <PortalButton btnTheme="outlined" onClick={selectAllGuests}>
                <Text.CopyMini color="quaternary" textTransform="uppercase" noMargins>
                  Select All
                </Text.CopyMini>
              </PortalButton>
            )}

            {clearable && (
              <PortalButton btnTheme="outlined" onClick={removeAll} disabled={disabledRemove}>
                <Text.CopyMini color={disabledRemove ? 'tertiary' : 'quaternary'} textTransform="uppercase" noMargins>
                  Clear All
                </Text.CopyMini>
              </PortalButton>
            )}

            {!clearable && enableActions && (
              <>
                <PortalButton btnTheme="outlined" onClick={removeAll} disabled={disabledRemove}>
                  <Text.CopyMini color={disabledRemove ? 'tertiary' : 'quaternary'} textTransform="uppercase" noMargins>
                    {`Remove ${actionType}`}
                  </Text.CopyMini>
                </PortalButton>

                <PortalButton btnTheme="tertiary" onClick={selectAll} disabled={disabledAdd}>
                  <Text.CopyMini color={disabledAdd ? 'tertiary' : 'secondary'} textTransform="uppercase" noMargins>
                    {`Add ${actionType}`}
                  </Text.CopyMini>
                </PortalButton>
              </>
            )}
          </SecondaryActionsWrapper>
        </Paper>
      )}
    </div>
  );
}

MultiSearchSelectMenu.propTypes = {
  name: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.shape({})),
  filteredOptions: PropTypes.arrayOf(PropTypes.shape({})),
  handleChange: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string,
  enableActions: PropTypes.bool,
  clearable: PropTypes.bool,
  inputProps: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  selectAll: PropTypes.func,
  removeAll: PropTypes.func,
  menuProps: PropTypes.shape({}).isRequired,
  minCharacters: PropTypes.number,
  allSelectable: PropTypes.bool,
  selectAllGuests: PropTypes.func,
  singleSelectOnly: PropTypes.bool,
  filteredTwoLayerOptions: PropTypes.shape({}),
  showSearchCount: PropTypes.bool
};
MultiSearchSelectMenu.defaultProps = {
  name: '',
  value: [],
  filteredOptions: [],
  filteredTwoLayerOptions: {},
  inputProps: '',
  enableActions: false,
  clearable: false,
  selectAll: () => {},
  removeAll: () => {},
  emptyMessage: 'No result(s) found.',
  minCharacters: 0,
  allSelectable: false,
  singleSelectOnly: false,
  selectAllGuests: () => {},
  showSearchCount: true
};

export default MultiSearchSelectMenu;
